/**
 * LanguageConfigSection Component
 *
 * Section for configuring source and target languages.
 * Reads state from DubbingContext — no props required.
 */

import { memo } from 'react';
import { Globe, Languages, Music } from 'lucide-react';
import { NLLBLanguageSelector, AudioLanguageSelector } from '../../common/LanguageSelector';
import { useTranslation } from '@/hooks/useTranslation';
import { useDubbing } from '../DubbingContext.context';

export const LanguageConfigSection = memo(function LanguageConfigSection() {
  const { t } = useTranslation();
  const {
    state,
    isUploading,
    isDubbing,
    handleSourceLanguageChange,
    handleVoiceConfigsChange,
    handleAudioTrackModeChange,
  } = useDubbing();

  const { mediaType, sourceLanguage, voiceConfigs, audioTrackMode } = state;

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 p-4 xs:p-6">
      <h3 className="text-base xs:text-lg font-semibold text-gray-900 dark:text-white mb-3 xs:mb-4 flex items-center space-x-2">
        <Globe className="h-4 w-4 xs:h-5 xs:w-5" />
        <span>{t('dubbing.sections.languageSettings', { defaultValue: 'Language Settings' })}</span>
      </h3>

      <div className="space-y-4 xs:space-y-6">
        {/* Source language */}
        <div>
          <label className="block text-xs xs:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 xs:mb-2">
            {/* Key chosen by media type — a conditional defaultValue never
                fires for a key that exists in the locale. */}
            {t(
              mediaType === 'audio'
                ? 'dubbing.fields.sourceLanguageAudio'
                : 'dubbing.fields.sourceLanguage'
            )}
          </label>
          <NLLBLanguageSelector
            values={sourceLanguage ? [sourceLanguage] : []}
            onMultiChange={(langs) => handleSourceLanguageChange(langs[0] || '')}
            maxSelection={1}
            // Auto-detect only exists in the AUDIO dubbing backend; the video
            // pipeline requires an explicit source language.
            showAutoDetect={mediaType === 'audio'}
            placeholder={
              mediaType === 'audio'
                ? t('dubbing.fields.selectSourceLanguage', {
                    defaultValue: 'Select language or use auto-detect',
                  })
                : t('dubbing.fields.selectSourceLanguageRequired', {
                    defaultValue: 'Select the source language',
                  })
            }
          />
        </div>

        {/* Target languages with voice configuration */}
        <div className="space-y-3 xs:space-y-4 p-3 xs:p-4 border border-gray-200 dark:border-secondary-600 rounded-lg bg-gray-50 dark:bg-secondary-700">
          <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-1 xs:gap-2">
            <h4 className="text-xs xs:text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center space-x-2">
              <Languages className="h-3.5 w-3.5 xs:h-4 xs:w-4" />
              <span>
                {t('dubbing.fields.targetLanguages', {
                  defaultValue: 'Target Languages (languages to dub into)',
                })}
              </span>
            </h4>
            <span className="text-[10px] xs:text-xs text-gray-600 dark:text-gray-400">
              {t('dubbing.fields.voiceConfigHint', {
                defaultValue: 'Select languages and configure voice for each',
              })}
            </span>
          </div>
          <p className="text-[10px] xs:text-xs text-gray-600 dark:text-gray-400 mb-2">
            {t('dubbing.fields.voiceConfigDescription', {
              defaultValue:
                'Select one or more target languages. You can customize the TTS provider and voice for each language to ensure natural pronunciation.',
            })}
          </p>
          <AudioLanguageSelector
            values={voiceConfigs}
            onChange={handleVoiceConfigsChange}
            disabled={isUploading || isDubbing}
            className="w-full"
          />
        </div>

        {/* Audio track handling mode (video only) */}
        {mediaType !== 'audio' && (
          <div className="space-y-3 p-3 xs:p-4 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <h4 className="text-xs xs:text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center space-x-2">
              <Music className="h-3.5 w-3.5 xs:h-4 xs:w-4" />
              <span>
                {t('dubbing.fields.audioTrackMode', {
                  defaultValue: 'Existing Audio Tracks',
                })}
              </span>
            </h4>
            <p className="text-[10px] xs:text-xs text-gray-600 dark:text-gray-400">
              {t('dubbing.fields.audioTrackModeDescription', {
                defaultValue:
                  'Choose how to handle existing audio tracks in the video when adding dubbed audio.',
              })}
            </p>
            <div className="space-y-2">
              {/* Add mode - preserve all existing tracks */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="audioTrackMode"
                  value="add"
                  checked={audioTrackMode === 'add'}
                  onChange={() => handleAudioTrackModeChange('add')}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  disabled={isUploading || isDubbing}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('dubbing.fields.audioTrackModeAdd', {
                      defaultValue: 'Add to existing tracks',
                    })}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('dubbing.fields.audioTrackModeAddDescription', {
                      defaultValue:
                        'Keep all existing audio tracks (e.g., English, Chinese) and add the dubbed tracks after them.',
                    })}
                  </p>
                </div>
              </label>
              {/* Overwrite mode - remove all existing tracks */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="audioTrackMode"
                  value="overwrite"
                  checked={audioTrackMode === 'overwrite'}
                  onChange={() => handleAudioTrackModeChange('overwrite')}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  disabled={isUploading || isDubbing}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('dubbing.fields.audioTrackModeOverwrite', {
                      defaultValue: 'Replace all tracks',
                    })}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('dubbing.fields.audioTrackModeOverwriteDescription', {
                      defaultValue:
                        'Remove all existing audio tracks and keep only the newly dubbed tracks.',
                    })}
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
